import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';


const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const ADMIN_TEST_HISTORY_API = process.env.REACT_APP_ADMIN_TEST_HISTORY_API || `${API_BASE_URL}/api/admin-app/test-history/`;

const AdminTestHistoryEditPage = () => {
    const navigate = useNavigate();
    const { testId } = useParams();
    const [test, setTest] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [form, setForm] = useState({ name: '', sections: [] });
    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');

    const headers = useMemo(() => {
        const token = (localStorage.getItem('guest_access_token') || sessionStorage.getItem('guest_access_token') || '').trim();
        if (!token) return null;
        return { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
    }, []);

    const detailUrl = `${ADMIN_TEST_HISTORY_API}${testId}/`;

    useEffect(() => {
        const fetchTest = async () => {
            if (!headers || !testId) {
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                const response = await fetch(detailUrl, { headers });
                const data = response.ok ? await response.json() : null;
                if (!data) {
                    setErrorMessage('Test not found.');
                    return;
                }
                setTest(data);
                
                // Initialize form with test data
                const sections = Array.isArray(data.sections) ? data.sections : [];
                setForm({
                    name: data.name || '',
                    sections: sections.length > 0
                        ? sections.map((section) => ({
                            ...section,
                            question_count: Number(section.question_count) || 1,
                        }))
                        : [],
                });
            } catch (error) {
                setErrorMessage('Unable to load test details.');
            } finally {
                setLoading(false);
            }
        };

        fetchTest();
    }, [headers, testId, detailUrl]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
        if (errorMessage) setErrorMessage('');
        if (successMessage) setSuccessMessage('');
    };

    const handleSectionChange = (index, field, value) => {
        setForm((prev) => {
            const sections = [...prev.sections];
            sections[index] = {
                ...sections[index],
                [field]: field === 'question_count' || field === 'time_minutes' ? Number(value) : value,
            };
            return { ...prev, sections };
        });
        if (errorMessage) setErrorMessage('');
        if (successMessage) setSuccessMessage('');
    };

    const handleRemoveSection = (index) => {
        setForm((prev) => ({
            ...prev,
            sections: prev.sections.filter((_, i) => i !== index),
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!headers) {
            setErrorMessage('Session expired. Please log in again.');
            return;
        }

        if (!form.name.trim()) {
            setErrorMessage('Test name is required.');
            return;
        }

        const payload = {
            name: form.name.trim(),
            sections: form.sections.map((s) => ({
                topic: (s.topic || '').trim(),
                question_count: Number(s.question_count) || 1,
                time_minutes: Number(s.time_minutes) || 1,
            })),
        };

        setSubmitting(true);
        try {
            const response = await fetch(detailUrl, {
                method: 'PATCH',
                headers,
                body: JSON.stringify(payload),
            });
            const data = await response.json().catch(() => ({}));
            if (!response.ok) {
                setErrorMessage(data?.error || 'Unable to update test.');
                return;
            }
            setSuccessMessage('Test updated successfully.');
            setTimeout(() => navigate('/admin/test-history?role=admin'), 1500);
        } catch (error) {
            setErrorMessage('Unable to reach server right now.');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="admin-panel-card admin-table-section">
                <div style={{ padding: '20px', textAlign: 'center' }}>Loading test details...</div>
            </div>
        );
    }

    if (!test) {
        return (
            <div className="admin-panel-card admin-table-section">
                <div className="admin-security-feedback admin-security-feedback-error">Test not found.</div>
                <button onClick={() => navigate('/admin/test-history?role=admin')} className="admin-profile-action-btn" style={{ marginTop: '12px' }}>
                    Back to Test History
                </button>
            </div>
        );
    }

    return (
        <div className="admin-panel-card admin-table-section">
            <div className="admin-college-header">
                <div>
                    <h3>Edit Test</h3>
                    <p className="admin-subtitle">Configure test name and section timing.</p>
                </div>
            </div>

            {errorMessage && <div className="admin-security-feedback admin-security-feedback-error">{errorMessage}</div>}
            {successMessage && <div className="admin-security-feedback admin-security-feedback-success">{successMessage}</div>}

            <form onSubmit={handleSubmit} style={{ padding: '20px' }}>
                <div style={{ marginBottom: '20px' }}>
                    <label className="admin-security-label" htmlFor="test-name">Test Name</label>
                    <input
                        id="test-name"
                        name="name"
                        type="text"
                        className="admin-security-input"
                        value={form.name}
                        onChange={handleInputChange}
                        disabled={submitting}
                        maxLength={255}
                    />
                </div>

                <div style={{ marginBottom: '20px' }}>
                    <div style={{ marginBottom: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <h4 style={{ margin: 0 }}>Section Timing</h4>
                    </div>

                    {form.sections.length > 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            {form.sections.map((section, index) => (
                                <div
                                    key={index}
                                    style={{
                                        display: 'grid',
                                        gridTemplateColumns: '2fr 120px 120px 40px',
                                        gap: '12px',
                                        alignItems: 'end',
                                        padding: '12px',
                                        border: '1px solid #e5e7eb',
                                        borderRadius: '6px',
                                        background: '#f9fafb',
                                    }}
                                >
                                    <div>
                                        <label className="admin-security-label">Topic</label>
                                        <input
                                            type="text"
                                            className="admin-security-input"
                                            placeholder="e.g., Quantitative, Logical Reasoning"
                                            value={section.topic}
                                            onChange={(e) => handleSectionChange(index, 'topic', e.target.value)}
                                            disabled={submitting}
                                        />
                                    </div>
                                    <div>
                                        <label className="admin-security-label">Time (mins)</label>
                                        <input
                                            type="number"
                                            min="1"
                                            max="180"
                                            className="admin-security-input"
                                            value={section.time_minutes}
                                            onChange={(e) => handleSectionChange(index, 'time_minutes', Number(e.target.value))}
                                            disabled={submitting}
                                        />
                                    </div>
                                    <div>
                                        <label className="admin-security-label">Questions</label>
                                        <input
                                            type="number"
                                            min="1"
                                            max="100"
                                            className="admin-security-input"
                                            value={section.question_count ?? 1}
                                            disabled={true}
                                            readOnly
                                        />
                                    </div>
                                    <button
                                        type="button"
                                        className="admin-profile-action-btn admin-profile-action-btn-danger"
                                        onClick={() => handleRemoveSection(index)}
                                        disabled={submitting}
                                        title="Remove section"
                                    >
                                        ✕
                                    </button>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div style={{ padding: '12px', background: '#f3f4f6', borderRadius: '6px', color: '#6b7280' }}>
                            No sections configured.
                        </div>
                    )}
                </div>

                <div className="admin-college-actions">
                    <button
                        type="submit"
                        className="admin-profile-action-btn admin-profile-action-btn-primary admin-college-primary-btn"
                        disabled={submitting}
                    >
                        {submitting ? 'Saving...' : 'Save Changes'}
                    </button>
                    <button
                        type="button"
                        className="admin-profile-action-btn"
                        onClick={() => navigate('/admin/test-history?role=admin')}
                        disabled={submitting}
                    >
                        Cancel
                    </button>
                </div>
            </form>
        </div>
    );
};

export default AdminTestHistoryEditPage;
